import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import api from '../../services/api';
import { normalizePricingRow } from '../../utils/pricing';
import {
  EMPTY_DELIVERY_ADDRESS,
  US_STATE_OPTIONS,
  deliveryAddressFromProfile,
  formatDeliveryAddressLine,
  isDeliveryAddressComplete,
} from '../../utils/addressFields';

const EMPTY_QUOTE = {
  diesel_quantity: '',
  def_quantity: '',
  dyed_quantity: '',
  frequency: 'one-time',
  delivery_fee: '',
  ...EMPTY_DELIVERY_ADDRESS,
  expires_at: '',
};

export default function QuoteCreationSection() {
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [customerSource, setCustomerSource] = useState('existing');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customCompanyName, setCustomCompanyName] = useState('');
  const [customerProfile, setCustomerProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [quoteData, setQuoteData] = useState(EMPTY_QUOTE);
  const [quoteMessage, setQuoteMessage] = useState('');
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [quotePdfFile, setQuotePdfFile] = useState(null);
  const [currentPricing, setCurrentPricing] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const createQuotePdfInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [customersRes, pricingRes] = await Promise.all([
          api.get('/managers/customers/'),
          api.get('/pricing/pricing/current/').catch(() => ({ data: null })),
        ]);
        const raw = customersRes.data;
        const list = Array.isArray(raw)
          ? raw
          : raw?.results || raw?.data || raw?.customers || [];
        setCustomers(list);
        if (pricingRes.data) {
          setCurrentPricing(normalizePricingRow(pricingRes.data));
        }
      } catch (error) {
        console.error('Error loading quote creation data:', error);
      } finally {
        setLoadingCustomers(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (customerSource !== 'existing' || !selectedCustomerId) {
      if (customerSource !== 'existing') {
        setCustomerProfile(null);
      }
      if (!selectedCustomerId) {
        setCustomerProfile(null);
        if (customerSource === 'existing') {
          setQuoteData(EMPTY_QUOTE);
        }
      }
      return;
    }

    (async () => {
      setLoadingProfile(true);
      setQuoteMessage('');
      try {
        const response = await api.get(`/managers/customer-detail/?customer_id=${selectedCustomerId}`);
        const profile = response.data?.profile || null;
        setCustomerProfile(profile);
        setQuoteData({
          ...EMPTY_QUOTE,
          ...deliveryAddressFromProfile(profile),
        });
        setQuotePdfFile(null);
        setCalculatedPrice(0);
      } catch (error) {
        console.error('Error loading customer profile:', error);
        setCustomerProfile(null);
        setQuoteMessage('Failed to load customer details.');
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, [selectedCustomerId, customerSource]);

  useEffect(() => {
    if (customerSource === 'company') {
      setQuoteData((prev) => ({
        ...EMPTY_QUOTE,
        delivery_address: prev.delivery_address,
        delivery_city: prev.delivery_city,
        delivery_state: prev.delivery_state,
        delivery_zip: prev.delivery_zip,
      }));
    }
  }, [customerSource]);

  useEffect(() => {
    if (!currentPricing) {
      setCalculatedPrice(0);
      return;
    }

    const dieselQty = parseFloat(quoteData.diesel_quantity) || 0;
    const defQty = parseFloat(quoteData.def_quantity) || 0;
    const dyedQty = parseFloat(quoteData.dyed_quantity) || 0;
    const deliveryFee = parseFloat(quoteData.delivery_fee) || 0;

    const dieselPrice = parseFloat(currentPricing.diesel_price_per_liter) || 0;
    const defPrice = parseFloat(currentPricing.def_price_per_liter) || 0;
    const dyedPrice = parseFloat(currentPricing.dyed_price_per_liter) || 0;
    const dieselTax = parseFloat(currentPricing.diesel_sales_tax) || 0;
    const defTax = parseFloat(currentPricing.def_sales_tax) || 0;
    const dyedTax = parseFloat(currentPricing.dyed_sales_tax) || 0;

    const dieselCost = dieselQty * dieselPrice;
    const defCost = defQty * defPrice;
    const dyedCost = dyedQty * dyedPrice;
    const dieselTaxAmount = dieselCost * dieselTax;
    const defTaxAmount = defCost * defTax;
    const dyedTaxAmount = dyedCost * dyedTax;

    setCalculatedPrice(
      dieselCost + dieselTaxAmount + defCost + defTaxAmount + dyedCost + dyedTaxAmount + deliveryFee
    );
  }, [
    quoteData.diesel_quantity,
    quoteData.def_quantity,
    quoteData.dyed_quantity,
    quoteData.delivery_fee,
    currentPricing,
  ]);

  const handleCreateQuotePdfChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setQuotePdfFile(null);
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setQuoteMessage('Please select a PDF file.');
      event.target.value = '';
      setQuotePdfFile(null);
      return;
    }
    setQuotePdfFile(file);
    setQuoteMessage('');
  };

  const handleCreateQuote = async () => {
    const companyName = customCompanyName.trim();
    if (customerSource === 'existing' && !selectedCustomerId) {
      setQuoteMessage('Select a customer or switch to company name.');
      return;
    }
    if (customerSource === 'company' && !companyName) {
      setQuoteMessage('Enter a company name.');
      return;
    }

    setCreatingQuote(true);
    setQuoteMessage('');

    try {
      const payload = {
        diesel_quantity: parseFloat(quoteData.diesel_quantity) || 0,
        def_quantity: parseFloat(quoteData.def_quantity) || 0,
        dyed_quantity: parseFloat(quoteData.dyed_quantity) || 0,
        frequency: quoteData.frequency,
        delivery_fee: parseFloat(quoteData.delivery_fee) || 0,
        delivery_address: formatDeliveryAddressLine(quoteData),
        expires_at: quoteData.expires_at || null,
      };

      if (customerSource === 'existing') {
        payload.customer = selectedCustomerId;
      } else {
        payload.quote_company_name = companyName;
      }

      if (quotePdfFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, value);
          }
        });
        formData.append('pdf_file', quotePdfFile);
        await api.post('/managers/create-quote/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/managers/create-quote/', payload);
      }

      setQuoteMessage('Quote created successfully!');
      setQuoteData({
        ...EMPTY_QUOTE,
        ...(customerSource === 'existing' ? deliveryAddressFromProfile(customerProfile) : {
          delivery_address: quoteData.delivery_address,
          delivery_city: quoteData.delivery_city,
          delivery_state: quoteData.delivery_state,
          delivery_zip: quoteData.delivery_zip,
        }),
      });
      if (customerSource === 'company') {
        setCustomCompanyName('');
      }
      setQuotePdfFile(null);
      if (createQuotePdfInputRef.current) {
        createQuotePdfInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error creating quote:', error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to create quote. Please check your input.';
      setQuoteMessage(errorMsg);
    } finally {
      setCreatingQuote(false);
    }
  };

  const hasRecipient =
    (customerSource === 'existing' && selectedCustomerId) ||
    (customerSource === 'company' && customCompanyName.trim());

  const showQuoteForm =
    customerSource === 'company' || (selectedCustomerId && !loadingProfile);

  const canCreate =
    hasRecipient &&
    isDeliveryAddressComplete(quoteData) &&
    (quotePdfFile ||
      ((quoteData.diesel_quantity || quoteData.def_quantity || quoteData.dyed_quantity) &&
        currentPricing));

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>
        Create Quote
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Build a quote for an existing customer or enter any company name, using current pricing or a custom PDF.
      </Typography>

      {quoteMessage && (
        <Alert
          severity={quoteMessage.includes('success') ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setQuoteMessage('')}
        >
          {quoteMessage}
        </Alert>
      )}

      {loadingCustomers ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ToggleButtonGroup
              value={customerSource}
              exclusive
              fullWidth
              onChange={(_, value) => {
                if (!value) return;
                setCustomerSource(value);
                setQuoteMessage('');
                if (value === 'company') {
                  setSelectedCustomerId('');
                  setCustomerProfile(null);
                } else {
                  setCustomCompanyName('');
                }
              }}
            >
              <ToggleButton value="existing">Existing customer</ToggleButton>
              <ToggleButton value="company">Company name</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {customerSource === 'existing' ? (
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="quote-customer-label">Customer</InputLabel>
                <Select
                  labelId="quote-customer-label"
                  label="Customer"
                  value={selectedCustomerId}
                  onChange={(e) => setSelectedCustomerId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a customer</em>
                  </MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name || customer.email || customer.id.substring(0, 8)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company name"
                value={customCompanyName}
                onChange={(e) => setCustomCompanyName(e.target.value)}
                helperText="Used on the quote and PDF — customer does not need an account yet"
              />
            </Grid>
          )}

          {loadingProfile && customerSource === 'existing' && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading customer details…
                </Typography>
              </Box>
            </Grid>
          )}

          {showQuoteForm && (
            <>
              {customerSource === 'existing' && selectedCustomer && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Creating quote for <strong>{selectedCustomer.company_name || 'customer'}</strong>
                  </Typography>
                </Grid>
              )}
              {customerSource === 'company' && customCompanyName.trim() && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Creating quote for <strong>{customCompanyName.trim()}</strong>
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Diesel quantity (gallons)"
                  type="number"
                  value={quoteData.diesel_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, diesel_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DEF quantity (gallons)"
                  type="number"
                  value={quoteData.def_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, def_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Dyed quantity (gallons)"
                  type="number"
                  value={quoteData.dyed_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, dyed_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={quoteData.frequency}
                    onChange={(e) => setQuoteData({ ...quoteData, frequency: e.target.value })}
                    label="Frequency"
                  >
                    <MenuItem value="one-time">One-time</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {currentPricing && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Current pricing
                    </Typography>
                    <Typography variant="body2">
                      Diesel: ${currentPricing.diesel_price_per_liter}/gal
                      {currentPricing.diesel_sales_tax > 0 &&
                        ` (${(parseFloat(currentPricing.diesel_sales_tax) * 100).toFixed(2)}% tax)`}
                      {' | '}
                      DEF: ${currentPricing.def_price_per_liter}/gal
                      {currentPricing.def_sales_tax > 0 &&
                        ` (${(parseFloat(currentPricing.def_sales_tax) * 100).toFixed(2)}% tax)`}
                      {' | '}
                      Dyed: ${currentPricing.dyed_price_per_liter}/gal
                      {currentPricing.dyed_sales_tax > 0 &&
                        ` (${(parseFloat(currentPricing.dyed_sales_tax) * 100).toFixed(2)}% tax)`}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Delivery fee ($)"
                  type="number"
                  value={quoteData.delivery_fee}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_fee: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Estimated price
                  </Typography>
                  <Typography variant="h6">${calculatedPrice.toFixed(2)}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Based on current prices and taxes
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#1E3A5F' }}>
                  Delivery location
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street address"
                  value={quoteData.delivery_address}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_address: e.target.value })}
                  required
                  placeholder="Enter street address"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={quoteData.delivery_city}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_city: e.target.value })}
                  required
                  placeholder="Enter city"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={quoteData.delivery_state}
                    onChange={(e) => setQuoteData({ ...quoteData, delivery_state: e.target.value })}
                    label="State"
                  >
                    {US_STATE_OPTIONS.map(([code, name]) => (
                      <MenuItem key={code} value={code}>
                        {code} - {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP code"
                  value={quoteData.delivery_zip}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_zip: e.target.value })}
                  required
                  placeholder="12345"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expires at (optional)"
                  type="date"
                  value={quoteData.expires_at}
                  onChange={(e) => setQuoteData({ ...quoteData, expires_at: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Quote PDF (optional)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                    {quotePdfFile ? 'Change PDF' : 'Choose PDF'}
                    <input
                      ref={createQuotePdfInputRef}
                      type="file"
                      hidden
                      accept="application/pdf,.pdf"
                      onChange={handleCreateQuotePdfChange}
                    />
                  </Button>
                  {quotePdfFile && (
                    <Typography variant="body2" color="text.secondary">
                      {quotePdfFile.name}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Upload a custom PDF, or leave empty to auto-generate from the details above.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleCreateQuote}
                  disabled={creatingQuote || !canCreate}
                >
                  {creatingQuote ? 'Creating…' : 'Create quote'}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Paper>
  );
}
